import { CookieKeys, getCookie, setCookie, removeCookie } from "@lib/cookies";
import { useCallback, useEffect, useState } from "react";

const useCookie = <T>(
  name: CookieKeys,
  defaultValue: T,
  coerceFunction: (value: string) => T | undefined,
): [
  T | undefined,
  (
    newValue: T,
    expiresInDays?: number | undefined | "session",
  ) => Promise<void>,
  () => Promise<void>,
] => {
  const [value, setValue] = useState<T | undefined>(undefined);

  useEffect(() => {
    (async () => {
      const cookieValue = await getCookie(name, defaultValue, coerceFunction);
      setValue(cookieValue);
    })();
  }, [name, defaultValue, coerceFunction]);

  const updateCookie = useCallback(
    async (newValue: T, expiresInDays?: number | undefined | "session") => {
      await setCookie(name, String(newValue), expiresInDays);
      setValue(newValue);
    },
    [name],
  );

  const deleteCookie = useCallback(async () => {
    await removeCookie(name);
    setValue(undefined);
  }, [name]);

  return [value, updateCookie, deleteCookie];
};

export { useCookie };
